import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { HStack } from "@whoosh/design-system/src/components/primitive";

import { useGetFirstLastTimeAcrossCoursesPatron } from "../hooks/useGetFirstLastTimeAcrossCoursesPatron";
import { SingleCourseColumn } from "./SingleCourseColumn";
import { CourseColumns_facilities$key } from "./__generated__/CourseColumns_facilities.graphql";

type CourseColumnsProps = {
  facilityRefs: CourseColumns_facilities$key;
  courseIds: string[];
  width: number | "100%";
  currentSlotId?: string;
  handleMultiSlotRowPress?: (slotId: string) => void;
  setBookingWizardSlotId?: (slotId: string) => void;
  setBookingWizardStartTime?: (startTime: string) => void;
};

export const CourseColumns = ({
  facilityRefs,
  courseIds,
  width,
  currentSlotId,
  handleMultiSlotRowPress,
  setBookingWizardSlotId,
  setBookingWizardStartTime,
}: CourseColumnsProps) => {
  const facilities = useFragment(CourseColumnsFragmentString, facilityRefs);

  const firstAndLastTime = useGetFirstLastTimeAcrossCoursesPatron({
    facilityRefs: facilities,
  });

  const agenda = facilities
    .flatMap((facility) => facility?.agendas?.edges ?? [])
    .find((edge) => edge?.node)?.node;

  const queueSlotId = agenda?.queueSlot.id || "";

  return (
    <HStack bg="surface-primary" position="relative" width={width}>
      {facilities
        .flatMap((facility) => facility?.courses?.edges ?? [])
        ?.filter((e) => e?.node?.id && courseIds.includes(e.node.id))
        .map((c) => {
          return c?.node ? (
            <SingleCourseColumn
              key={c.node.id}
              courseRef={c.node}
              firstAndLastTime={firstAndLastTime}
              courseName={c.node.name}
              queueSlotId={queueSlotId}
              {...{
                currentSlotId,
                handleMultiSlotRowPress,
                setBookingWizardSlotId,
                setBookingWizardStartTime,
              }}
            />
          ) : null;
        })}
    </HStack>
  );
};

export const CourseColumnsFragmentString = graphql`
  fragment CourseColumns_facilities on Facility @relay(plural: true) {
    id
    name
    ...useGetFirstLastTimeAcrossCoursesPatron_facilities
    courses(courseIds: $courseIds) {
      count
      edges {
        node {
          id
          name
          ...SingleCourseColumn_course
        }
      }
    }
    agendas(dateGte: $date, dateLte: $date) {
      edges {
        node {
          id
          queueSlot {
            id
          }
        }
      }
    }
  }
`;
